import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  useTheme,
  alpha,
  Fade,
  Zoom,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  AutoAwesome,
  Security,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const LoginForm: React.FC = () => {
  const theme = useTheme();
  const { login, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [expandedLab, setExpandedLab] = useState<string | null>(null);

  // Laboratory accounts data removed for security - manual login required

  // Laboratory groups for organized email shortcuts (Zgounder site)
  const labGroups = [
    {
      id: 'admin',
      name: '👨‍💼 Administration',
      color: theme.palette.primary.main,
      accounts: [
        { email: 'admin@zgounder.ma', label: '👨‍💼 Admin' },
        { email: 'reception@zgounder.ma', label: '📥 Réception' },
        { email: 'preparation@zgounder.ma', label: '⚙️ Préparation Mécanique' },
      ]
    },
    {
      id: 'saa',
      name: '🧪 Laboratoire SAA',
      color: theme.palette.success.main,
      accounts: [
        { email: 'saa.prep@zgounder.ma', label: '🧪 SAA Préparation Chimique' },
        { email: 'saa.analyst@zgounder.ma', label: '🔬 SAA Analyste' },
        { email: 'sup.saa@zgounder.ma', label: '✅ SAA Superviseur' },
      ]
    },
    {
      id: 'icp',
      name: '🔬 Laboratoire ICP',
      color: theme.palette.info.main,
      accounts: [
        { email: 'icp.prep@zgounder.ma', label: '🧪 ICP Préparation Chimique' },
        { email: 'icp.analyst@zgounder.ma', label: '🔬 ICP Analyste' },
        { email: 'sup.icp@zgounder.ma', label: '✅ ICP Superviseur' },
      ]
    },
    {
      id: 'physico',
      name: '🧪 Labo Physico-Chimique',
      color: '#059669',
      accounts: [
        { email: 'analyst.physico@zgounder.ma', label: '🧪 Physico Analyste' },
        { email: 'sup.physico@zgounder.ma', label: '✅ Physico Superviseur' },
      ]
    },
    {
      id: 'client',
      name: '🌐 Portail Client',
      color: theme.palette.secondary.main,
      accounts: [
        { email: 'client@zgounder.ma', label: '🌐 Portail Client' },
      ]
    }
  ];

  const handleLabToggle = (labId: string) => {
    setExpandedLab(expandedLab === labId ? null : labId);
  };

  const handleEmailSelect = (selectedEmail: string) => {
    setEmail(selectedEmail);
    setPassword('admin123');
    setExpandedLab(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.message || 'Erreur de connexion');
    }
  };

  // Auto-fill functions removed for security

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.dark} 100%)`,
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, ${alpha(theme.palette.secondary.main, 0.3)} 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, ${alpha(theme.palette.primary.light, 0.3)} 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, ${alpha(theme.palette.info.main, 0.2)} 0%, transparent 50%)
          `,
        },
      }}
    >
      {/* Floating Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
          backdropFilter: 'blur(20px)',
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-20px)' },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '70%',
          right: '15%',
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.2)}, ${alpha(theme.palette.primary.light, 0.1)})`,
          backdropFilter: 'blur(20px)',
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />

      <Fade in timeout={800}>
        <Card 
          sx={{ 
            maxWidth: 700, 
            width: '100%',
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.9)})`,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            borderRadius: 6,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
            },
          }}
        >
        <CardContent sx={{ p: 5 }}>
          {/* Header */}
          <Zoom in timeout={1000}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 150, // Increased from 120 to 150 - NO MASK
                  height: 150, // Increased from 120 to 150 - NO MASK
                  mx: 'auto',
                  mb: 2, // Reduced margin bottom
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.05)' },
                  },
                }}
              >
                <img 
                  src="/logo2.png" 
                  alt="AFRILAB Logo" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' 
                  }} 
                />
              </Box>
              
              {/* Beautiful LIMS System text directly under logo */}
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  fontWeight: 700,
                  mb: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textAlign: 'center',
                  letterSpacing: '0.02em',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                LIMS System
            </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                Laboratoire d'Analyses Minières - Maroc
            </Typography>
          </Box>
          </Zoom>

          {/* Security Notice */}
          <Fade in timeout={1200}>
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{ 
                  p: 3,
                  borderRadius: 3,
                  background: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  textAlign: 'center',
                }}
              >
                <Security sx={{ 
                  color: theme.palette.info.main, 
                  fontSize: 48, 
                  mb: 2,
                }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: theme.palette.info.main,
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  🔐 Connexion Sécurisée
            </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  Saisissez vos identifiants pour accéder au système LIMS AFRILAB
                </Typography>
              </Box>
            </Box>
          </Fade>

          <Divider 
            sx={{ 
              mb: 3,
              '&::before, &::after': {
                borderColor: alpha(theme.palette.divider, 0.3),
              },
            }} 
          />

          {/* Email Shortcuts - Grouped by Laboratory */}
          <Fade in timeout={1400}>
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 2, 
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  textAlign: 'center'
                }}
              >
                🚀 Raccourcis Email
              </Typography>
              <Box sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.2)}`, borderRadius: 2, overflow: 'hidden' }}>
                {labGroups.map((lab, index) => (
                  <Box key={lab.id}>
                    <ListItemButton
                      onClick={() => handleLabToggle(lab.id)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        background: alpha(lab.color, 0.05),
                        borderBottom: index < labGroups.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
                        '&:hover': {
                          background: alpha(lab.color, 0.1),
                        },
                      }}
                    >
                      <ListItemText 
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500, color: lab.color }}>
                            {lab.name}
                          </Typography>
                        }
                      />
                      {expandedLab === lab.id ? <ExpandLess sx={{ color: lab.color }} /> : <ExpandMore sx={{ color: lab.color }} />}
                    </ListItemButton>
                    <Collapse in={expandedLab === lab.id} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {lab.accounts.map((account) => (
                          <ListItemButton
                            key={account.email}
                            onClick={() => handleEmailSelect(account.email)}
                            sx={{
                              pl: 4,
                              py: 1,
                              background: alpha(lab.color, 0.02),
                              '&:hover': {
                                background: alpha(lab.color, 0.08),
                              },
                            }}
                          >
                            <ListItemText 
                              primary={
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                  {account.label}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                                  {account.email}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                ))}
              </Box>
            </Box>
          </Fade>

          {/* Login Form */}
          <Fade in timeout={1600}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              placeholder="utilisateur@zgounder.ma"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: alpha(theme.palette.background.paper, 0.9),
                    },
                    '&.Mui-focused': {
                      background: alpha(theme.palette.background.paper, 1),
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  },
                }}
            />

            <TextField
              fullWidth
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: alpha(theme.palette.background.paper, 0.9),
                    },
                    '&.Mui-focused': {
                      background: alpha(theme.palette.background.paper, 1),
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  },
                }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                        sx={{
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            background: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          },
                        }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
                <Fade in>
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mt: 2,
                      borderRadius: 3,
                      background: alpha(theme.palette.error.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    }}
                  >
                {error}
              </Alert>
                </Fade>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                sx={{ 
                  mt: 3, 
                  mb: 2, 
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  boxShadow: `0 8px 25px -8px ${theme.palette.primary.main}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 35px -8px ${theme.palette.primary.main}`,
                  },
                  '&:disabled': {
                    background: alpha(theme.palette.action.disabled, 0.3),
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                {isLoading ? 'Connexion en cours...' : 'Se connecter'}
            </Button>
          </form>
          </Fade>

          {/* Footer */}
          <Fade in timeout={2000}>
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  background: alpha(theme.palette.background.default, 0.5),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <AutoAwesome sx={{ fontSize: 16 }} />
              AFRILAB © 2024 - Laboratoire Minier Maroc
            </Typography>
          </Box>
            </Box>
          </Fade>
        </CardContent>
      </Card>
      </Fade>
    </Box>
  );
};

export default LoginForm; 